import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  COUNTRY_GROUP_STACK_ZOOM_THRESHOLD,
  IS_VC_COUNTRY_GROUPING_ENABLED,
  MIN_SWIMLANE_BOTTOM_GAP,
  VALUE_CHAIN_MAP,
} from 'constants/general';
import { VALUE_CHAIN_MAP_CUSTOM_NODES } from 'constants/interfaces';

import {
  applyCountryGroupingToNodes,
  applyUnitHidingToNodes,
  getCurrentUnitVisualHeight,
  getNodeAbsoluteY,
} from '../utils/valueChainNodeLayout';

import type { Node } from '@xyflow/react';
import type { ICustomUnitNodeData } from '../interfaces/ICustomUnitNode';
import type {
  IUseValueChainBoardNodesParams,
  IUseValueChainBoardNodesReturn,
} from '../interfaces/IUseValueChainBoardNodes';

const useValueChainBoardNodes = ({
  nodes,
  setNodes,
  setEdges,
  nodesRef,
  prevNodesSnapshotRef,
  groupedDataRef,
  boardData,
  isDraftReviewMode,
  isScenarioViewMode,
  isDraftBoardBuilding,
  isGroupingMode,
  isNodeDraggingRef,
  hasCompletedInitialLayoutRef,
  viewportSizeRef,
  viewportYRef,
  viewportZoomRef,
  createNodesAndEdges,
  applyAIPanelStateToNodes,
}: IUseValueChainBoardNodesParams): IUseValueChainBoardNodesReturn => {
  const [accumulatedMaxBoardHeight, setAccumulatedMaxBoardHeight] = useState(VALUE_CHAIN_MAP.TITLE_HEIGHT * 2);

  // Translates the structural hidden flags (collapsed / country-group stacked) onto
  // node.hidden. Viewport culling is implemented by ReactFlow
  const applyViewportCullingToDisplayNodes = useCallback((
    nodesToDisplay: Node[],
  ): Node[] => {
    if (
      isDraftReviewMode
      || !hasCompletedInitialLayoutRef.current
      || isNodeDraggingRef.current
      || !viewportSizeRef.current
      || viewportZoomRef.current <= 0
    ) {
      return nodesToDisplay;
    }

    return applyUnitHidingToNodes(nodesToDisplay);
  }, [
    hasCompletedInitialLayoutRef,
    isDraftReviewMode,
    isNodeDraggingRef,
    viewportSizeRef,
    viewportZoomRef,
  ]);

  const lastBoardDataRef = useRef<typeof boardData>(undefined);
  const lastIsDraftReviewModeRef = useRef<boolean>(false);

  const getCurrentCountryGroupingViewportRange = useCallback((): {
    top: number,
    bottom: number,
  } | undefined => {
    const currentViewportSize = viewportSizeRef.current;
    const currentViewportZoom = viewportZoomRef.current;

    if (
      isScenarioViewMode
      || !hasCompletedInitialLayoutRef.current
      || !currentViewportSize
      || currentViewportZoom <= 0
    ) {
      return undefined;
    }

    const currentViewportY = viewportYRef.current;

    return {
      top: -currentViewportY / currentViewportZoom,
      bottom: (currentViewportSize.height - currentViewportY) / currentViewportZoom,
    };
  }, [
    hasCompletedInitialLayoutRef,
    isScenarioViewMode,
    viewportSizeRef,
    viewportYRef,
    viewportZoomRef,
  ]);

  useEffect(() => {
    if (isGroupingMode) {
      lastBoardDataRef.current = undefined;
      return;
    }

    if (!boardData) {
      return;
    }

    const isBoardDataChanged = lastBoardDataRef.current !== boardData;
    const isDraftReviewModeChanged = lastIsDraftReviewModeRef.current !== isDraftReviewMode;
    lastBoardDataRef.current = boardData;
    lastIsDraftReviewModeRef.current = isDraftReviewMode;

    if (!isBoardDataChanged && !isDraftReviewModeChanged) {
      setNodes((prevNodes) => applyAIPanelStateToNodes(prevNodes));
      return;
    }

    if (!isBoardDataChanged && isDraftReviewModeChanged) {
      setNodes((prevNodes) => {
        let hasChanges = false;
        const nextNodes = prevNodes.map((node) => {
          const nodeData = node.data as unknown as { isDraftReviewMode?: boolean };
          if (nodeData.isDraftReviewMode === isDraftReviewMode) {
            return node;
          }
          hasChanges = true;
          return {
            ...node,
            data: {
              ...nodeData,
              isDraftReviewMode,
            } as unknown as Record<string, unknown>,
          };
        });
        return hasChanges ? applyAIPanelStateToNodes(nextNodes) : prevNodes;
      });
      return;
    }

    const applyBoardRebuild = () => {
      const {
        nodes: newNodes,
        edges: newEdges,
        accumulatedMaxBoardHeight: nextAccumulatedMaxBoardHeight,
      } = createNodesAndEdges(
        boardData.data.boards,
        prevNodesSnapshotRef.current,
        isDraftReviewMode,
      );
      const countryGroupingViewportRange = getCurrentCountryGroupingViewportRange();

      setAccumulatedMaxBoardHeight(nextAccumulatedMaxBoardHeight);
      setNodes((prevNodes) => {
        const hasActiveCountryGroups = prevNodes.some((prevNode) => {
          if (prevNode.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
            return false;
          }

          const prevNodeData = prevNode.data as unknown as ICustomUnitNodeData;
          return Boolean(prevNodeData.countryGroupId || prevNodeData.isHiddenByCountryGroup);
        });
        const shouldGroupByZoom = viewportZoomRef.current <= COUNTRY_GROUP_STACK_ZOOM_THRESHOLD;
        const shouldKeepCountryGroups = IS_VC_COUNTRY_GROUPING_ENABLED && Boolean(
          countryGroupingViewportRange && (shouldGroupByZoom || hasActiveCountryGroups),
        );

        const getNodesWithCurrentDisplayState = (
          nodesToDisplay: Node[],
        ): Node[] => {
          const nodesWithPanelState = applyAIPanelStateToNodes(nodesToDisplay);
          let nodesWithDisplayState = nodesWithPanelState;

          if (shouldKeepCountryGroups && countryGroupingViewportRange) {
            // eslint-disable-next-line no-param-reassign
            groupedDataRef.current = boardData;

            nodesWithDisplayState = hasActiveCountryGroups
              ? applyCountryGroupingToNodes(nodesWithPanelState)
              : applyCountryGroupingToNodes(
                nodesWithPanelState,
                countryGroupingViewportRange.top,
                countryGroupingViewportRange.bottom,
              );
          }

          return applyViewportCullingToDisplayNodes(nodesWithDisplayState);
        };

        if (!prevNodes || prevNodes.length === 0) {
          return getNodesWithCurrentDisplayState(newNodes);
        }

        const prevUnitNodesById = new Map<string, Node>();

        prevNodes.forEach((prevNode) => {
          if (prevNode.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
            prevUnitNodesById.set(prevNode.id, prevNode);
          }
        });

        const nextNodes = newNodes.map((node) => {
          if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
            return node;
          }

          const prevNode = prevUnitNodesById.get(node.id);
          if (!prevNode) {
            return node;
          }

          const prevData = prevNode.data as unknown as ICustomUnitNodeData;
          const nextData = node.data as unknown as ICustomUnitNodeData;

          const mergedData: ICustomUnitNodeData = {
            ...nextData,
          };

          mergedData.isCollapsed = typeof prevData.isCollapsed === 'boolean'
            ? prevData.isCollapsed
            : nextData.isCollapsed;

          mergedData.isHiddenByParent = nextData.isHiddenByParent;

          // Carry the previously measured size across the rebuild so ReactFlow keeps valid
          // handle bounds for cards that are off-screen (not re-rendered/re-measured) —
          // otherwise their connections cannot be positioned and disappear until panned to.
          return {
            ...node,
            position: node.position,
            measured: prevNode.measured,
            data: mergedData as unknown as Record<string, unknown>,
          };
        });

        return getNodesWithCurrentDisplayState(nextNodes);
      });
      setEdges(newEdges);
    };

    applyBoardRebuild();
  }, [
    applyAIPanelStateToNodes,
    applyViewportCullingToDisplayNodes,
    boardData,
    createNodesAndEdges,
    getCurrentCountryGroupingViewportRange,
    groupedDataRef,
    isDraftReviewMode,
    isGroupingMode,
    prevNodesSnapshotRef,
    setEdges,
    setNodes,
    viewportZoomRef,
  ]);

  const nodesHeightKey = useMemo(() => nodes
    .filter((node) => node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT)
    .map((node) => {
      const d = node.data as unknown as ICustomUnitNodeData;
      return [
        node.id,
        node.position.y,
        node.parentId ?? '',
        d.isHiddenByParent ? '1' : '0',
        d.isHiddenByCountryGroup ? '1' : '0',
        d.isCollapsed ? '1' : '0',
        d.totalChildrenHeight ?? '',
        d.tempTotalChildrenHeight ?? '',
        d.isCountryGroupLeader ? '1' : '0',
        d.isCountryGroupCollapsed ? '1' : '0',
      ].join(':');
    })
    .join('|'), [nodes]);

  useEffect(() => {
    if (isGroupingMode || isDraftBoardBuilding) {
      return;
    }

    const currentNodes = nodesRef.current;

    if (!currentNodes.length) {
      return;
    }

    const nodesById = new Map<string, Node>();
    currentNodes.forEach((node) => {
      nodesById.set(node.id, node);
    });

    let nextHeight = VALUE_CHAIN_MAP.TITLE_HEIGHT * 2;

    currentNodes.forEach((node) => {
      if (node.type !== VALUE_CHAIN_MAP_CUSTOM_NODES.UNIT) {
        return;
      }

      const unitData = node.data as unknown as ICustomUnitNodeData;
      if (unitData.isHiddenByParent || unitData.isHiddenByCountryGroup) {
        return;
      }

      const absoluteBottom = getNodeAbsoluteY(node, nodesById) + getCurrentUnitVisualHeight(unitData);
      nextHeight = Math.max(
        nextHeight,
        absoluteBottom + VALUE_CHAIN_MAP.BOARD_PADDING + MIN_SWIMLANE_BOTTOM_GAP,
      );
    });

    setAccumulatedMaxBoardHeight((prevHeight) => (
      prevHeight === nextHeight ? prevHeight : nextHeight
    ));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDraftBoardBuilding, nodesHeightKey]);

  useEffect(() => {
    if (isGroupingMode) {
      return;
    }

    // update boards and swimlanes while new nodes are added
    setNodes((nodesData) => {
      let hasChanges = false;
      const nextNodes = nodesData.map((node) => {
        const isBoardOrSwimlane = node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.BOARD
          || node.type === VALUE_CHAIN_MAP_CUSTOM_NODES.SWIMLANE;

        if (!isBoardOrSwimlane || node.data.accumulatedMaxBoardHeight === accumulatedMaxBoardHeight) {
          return node;
        }

        hasChanges = true;
        return {
          ...node,
          data: {
            ...node.data,
            accumulatedMaxBoardHeight,
          },
        };
      });

      return hasChanges ? nextNodes : nodesData;
    });
  }, [accumulatedMaxBoardHeight, setNodes, boardData, isGroupingMode]);

  return { applyViewportCullingToDisplayNodes };
};

export default useValueChainBoardNodes;
